import type { ImportTaxonomyNodeInput } from '@/api/api';
import { DEPTH_COLORS } from '../TaxonomyTree/taxonomyTreeConstants';
import { FolderOpen, Label } from '@mui/icons-material';
import { Alert, Box, Button, Chip, CircularProgress, DialogActions, DialogContent, Typography } from '@mui/material';
import type { ParsedTaxonomyImport } from './taxonomyImportParser';
import { TaxonomyImportPageObjectModel } from '@/poms/taxonomyImportPageObjectModel';

const getDepthColor = (depth: number) => DEPTH_COLORS[depth % DEPTH_COLORS.length];

function PreviewNode({ node, depth }: Readonly<{ node: ImportTaxonomyNodeInput; depth: number }>) {
  const hasChildren = node.children.length > 0;
  const color = getDepthColor(depth);

  return (
    <Box>
      <Box sx={{ pl: depth * 3, py: 0.25 }}>
        <Chip
          icon={
            hasChildren ? (
              <FolderOpen sx={{ fontSize: 16, color: `${color} !important` }} />
            ) : (
              <Label sx={{ fontSize: 16, color: `${color} !important` }} />
            )
          }
          label={node.name}
          size='small'
          variant='outlined'
          sx={{
            borderColor: color,
            color: 'text.primary',
            fontWeight: hasChildren ? 500 : 400,
            '& .MuiChip-label': { px: 1 },
            height: 28,
          }}
        />
      </Box>
      {node.children.map(child => (
        <PreviewNode key={child.name} node={child} depth={depth + 1} />
      ))}
    </Box>
  );
}

interface ImportPreviewStepProperties {
  parsedData: ParsedTaxonomyImport;
  isImporting: boolean;
  importError?: string;
  actionLabel?: string;
  onBack: () => void;
  onImport: () => void;
}

export const ImportPreviewStep = ({
  parsedData,
  isImporting,
  importError,
  actionLabel = 'Import',
  onBack,
  onImport,
}: ImportPreviewStepProperties) => (
  <>
    <DialogContent>
      {importError && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {importError}
        </Alert>
      )}
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        {parsedData.totalNodeCount} total, {parsedData.maxDepth} levels deep
      </Typography>
      <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
        {parsedData.tree.map(node => (
          <PreviewNode key={node.name} node={node} depth={0} />
        ))}
      </Box>
    </DialogContent>
    <DialogActions>
      <Button onClick={onBack} data-testid={TaxonomyImportPageObjectModel.PreviewBackButton}>
        Back
      </Button>
      <Button
        variant='contained'
        onClick={onImport}
        disabled={isImporting}
        sx={{ minWidth: '100px' }}
        data-testid={TaxonomyImportPageObjectModel.PreviewImportButton}
      >
        {isImporting ? <CircularProgress size={20} /> : actionLabel}
      </Button>
    </DialogActions>
  </>
);
